import React from "react";
import { Flex, Text, Icon, Spinner, Tooltip } from "@chakra-ui/react";
import { FaUser } from "react-icons/fa";
import {
  primary,
  white,
  textSecondary,
  textPrimary,
  newBorder,
  buttonAltGrey,
  buttonAltYellow,
  buttonGreen,
  buttonRed,
  buttonRedDark,
  buttonYellow,
  buttonAltYellowDark,
  color3,
  color2,
  color4,
  color5,
  color6,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import {
  FaRegCircle,
  FaCircleCheck,
  FaClock,
  FaCircleXmark,
  FaCircleHalfStroke,
} from "react-icons/fa6";
import useThemeColors from "../../../../hooks/useThemeColors";
import useNumberFormatter from "../../../../hooks/useNumberFormatter";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";

const TreatmentPlanStatusTotalItem = ({
  status,
  count,
  totalPrice,
  totalCompletedServicesPrice,
  loading,
  currency,
}) => {
  const { t } = useTranslation();
  const translate = (key) => t(key);
  const { whiteMode, newBorderMode, textPrimaryMode } = useThemeColors();
  const formattedNumber = useNumberFormatter();

  const currencySymbol = useCurrencySymbol(currency);

  const options = [
    {
      value: "draft",
      label: translate("status.draft"),
      icon: FaRegCircle,
      bg: "buttonAltGrey",
      bgDark: textSecondary,
      color: textSecondary,
      borderColor: newBorder,
      bgHover: "#ddd",
    },
    {
      value: "planning",
      label: translate("status.treatmentPlanPlanning"),
      icon: FaClock,
      bg: color2,
      bgDark: textPrimary,
      color: white,
      borderColor: buttonYellow,
      bgHover: "#9ebfc2",
    },
    {
      value: "approved",
      label: translate("status.treatmentPlanApproved"),
      icon: FaCircleCheck,
      bg: color3,
      bgDark: textPrimary,
      color: white,
      borderColor: buttonGreen,
      bgHover: "#9ebfc2",
    },
    {
      value: "ongoing",
      label: translate("status.ongoing"),
      icon: FaCircleHalfStroke,
      bg: color4,
      bgDark: buttonAltYellowDark,
      color: white,
      borderColor: buttonAltYellow,
      bgHover: "#9ebfc2",
    },
    {
      value: "completed",
      label: translate("status.treatmentPlanCompleted"),
      icon: FaCircleCheck,
      bg: color5,
      bgDark: textPrimary,
      color: textPrimary,
      borderColor: buttonGreen,
      bgHover: "#9ebfc2",
    },
    {
      value: "cancelled",
      label: translate("status.treatmentPlanCancelled"),
      icon: FaCircleXmark,
      bg: color6,
      bgDark: buttonRedDark,
      color: white,
      borderColor: buttonRed,
      bgHover: "#dda3a3",
    },
  ];

  // Find the option object that matches the status
  const statusOption = options.find((option) => option.value === status);

  // Fallback style and icon if status is not found in options
  const fallbackStyle = {
    bg: buttonAltGrey,
    bgDark: textSecondary,
    color: textSecondary,
    icon: FaUser, // Default icon
    label: "Unknown", // Default label
  };

  // Determine if the opacity should be lowered (when count is 0)
  const isLowOpacity = count === 0;

  const getPlanWord = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (count === 1 || (lastDigit === 1 && lastTwoDigits !== 11)) {
      return t("dashboard.plansOne"); // Singular
    } else if (
      lastDigit >= 2 &&
      lastDigit <= 9 &&
      !(lastTwoDigits >= 11 && lastTwoDigits <= 19)
    ) {
      return t("dashboard.plansPlural"); // Plural nominative
    } else {
      return t("dashboard.plansWho"); // Plural genitive
    }
  };

  // LOGS

  return (
    <Tooltip
      p="0.5rem 1rem"
      borderRadius="1rem"
      placement="top"
      label={
        <Flex direction="column" gap={1}>
          <Flex align="center" justify="space-between">
            <Text fontSize="xs" fontWeight="500" color={white} mr={2}>
              {t("stats.totalServicesPrice")}:
            </Text>
            <Text fontSize="xs" fontWeight="600" color={white}>
              {currencySymbol} {formattedNumber(totalPrice)}
            </Text>
          </Flex>

          <Flex align="center" justify="space-between">
            <Text fontSize="xs" fontWeight="500" color={white} mr={2}>
              {t("stats.totalCompletedServicesPrice")}:
            </Text>
            <Text fontSize="xs" fontWeight="600" color={white}>
              {formattedNumber(totalCompletedServicesPrice)}
            </Text>
          </Flex>
        </Flex>
      }
    >
      <Flex
        w="100%"
        borderTop={`1px solid ${newBorderMode}`}
        _last={{ borderRight: "none" }}
        bg={whiteMode}
        borderRadius="0rem"
        p={["8px 0rem", "10.5px 0rem"]}
        // h="33px"
        opacity={isLowOpacity ? "0.6" : "1"}
        align="center"
        justify="space-between"
        // mb="2px"
      >
        <Flex justify="flex-start" align="center" m="0">
          <Flex
            bg={statusOption ? statusOption.bg : fallbackStyle.bg}
            borderRadius="0.75rem"
            w="1.35rem"
            h="1.35rem"
            justify="center"
            align="center"
            textAlign="center"
          >
            <Icon
              as={statusOption ? statusOption.icon : fallbackStyle.icon}
              color={statusOption ? statusOption.color : fallbackStyle.color}
              fontSize="0.6rem"
              m="0 auto"
            />
          </Flex>
          {!loading ? (
            <Text
              ml={2}
              color={textPrimaryMode}
              fontSize="0.8rem"
              fontWeight="500"
            >
              {statusOption ? statusOption.label : fallbackStyle.label}
            </Text>
          ) : (
            <Spinner size="md" color={primary} />
          )}
        </Flex>

        <Flex align="center">
          <Text
            fontSize="0.8rem"
            fontWeight="500"
            color={textPrimaryMode}
            mr="0.25rem"
          >
            {count}
            <Text as="span" color={textSecondary} ml="0.25rem">
              {getPlanWord(count)}
            </Text>
          </Text>
        </Flex>
      </Flex>
    </Tooltip>
  );
};

export default TreatmentPlanStatusTotalItem;
